import {
  Component,
  ElementRef,
  EventEmitter,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  Output,
  SimpleChanges,
  ViewChild,
} from '@angular/core'
import {
  FormArray,
  FormControl,
  FormGroup, Validators,
} from '@angular/forms'
import {
  ApiService,
  AppointmentSelectOption,
  ChargeDescriptionMaster,
  ChargeDescriptionMasterData,
  ChargeRowFormGroup,
  Clinic,
  ClinicService,
  EstimateCharge,
  EstimateChargeDiscountType,
  EstimateService,
  PermissionsService,
} from '@mmx/shared'
import { last } from 'lodash'
import { BehaviorSubject, combineLatest, map, Observable, of, startWith, Subject, switchMap, takeUntil } from 'rxjs'
import { catchError, combineLatestWith, debounceTime, distinctUntilChanged, shareReplay, tap } from 'rxjs/operators'

// One form shape for every charge picker: this component builds rows, ChargeFormService reads them
// back, and a field missing from either side is silently dropped on save.
export type { ChargeRowFormGroup }

@Component({
  selector: 'mmx-charges-table-form',
  templateUrl: './charges-table-form.component.html',
  styleUrls: ['./charges-table-form.component.scss'],
  standalone: false,
})
export class ChargesTableFormComponent implements OnInit, OnChanges, OnDestroy {
  @ViewChild('chargeList') chargeListElement: ElementRef
  @Input() facilityIds: string[]
  @Input({ required: true }) chargesFormArray: FormArray<FormGroup<ChargeRowFormGroup>>
  @Input() showDiscountControls = true
  @Input() showVisitNumbers = true
  /** true → always render the free-text input; false → branch on appointmentOptions selections. */
  @Input() forceFreeTextVisitNumbers = false
  /** Validator upper bound. null falls back to `appointmentVisitNumbers.length`. */
  @Input() maxVisitNumber: number | null = null
  /** View-only mode: disables the editable row controls and hides add/edit/delete. */
  @Input() readOnly = false
  private _appointmentVisitNumbers?: number[]
  @Input()
  public set appointmentOptions(options: AppointmentSelectOption[]) {
    if (!options) {
      return
    }
    const selectedVisitNumbers = options
      .filter(o => o.selected && o.visitNumber)
      .map(o => o.visitNumber)

    if (this.forceFreeTextVisitNumbers) {
      this._appointmentVisitNumbers = selectedVisitNumbers
      this.noAppointmentsSelected = true
    } else if (selectedVisitNumbers.length > 0) {
      this._appointmentVisitNumbers = selectedVisitNumbers
      this.noAppointmentsSelected = false
    } else {
      this._appointmentVisitNumbers = []
      this.noAppointmentsSelected = true
    }
    this.visitNumbersValidator()

  }
  public get appointmentVisitNumbers(): number[] {
    return this._appointmentVisitNumbers ?? []
  }
  @Output() valueUpdated = new EventEmitter()
  @Output() addChargeDiscountControl = new EventEmitter<{ index: number; type: EstimateChargeDiscountType }>()
  @Output() removeChargeDiscountControl = new EventEmitter<{ index: number }>()
  public displayedColumns = ['type', 'description', 'cptCode', 'modifiers', 'unit', 'actionsColumn']
  public ChargeDescriptionMaster = ChargeDescriptionMaster
  public EstimateChargeDiscountType = EstimateChargeDiscountType
  public clinic: Clinic
  public chargeMissingVisitNumber = false
  public allVisitNumbersApplied = true
  public noAppointmentsSelected = false
  public invalidInputString = false

  public filterChargeCtrl = new FormControl()
  public loading$: BehaviorSubject<boolean> = new BehaviorSubject(true)
  private changes$ = new Subject<void>()
  private readonly editingIndex$ = new BehaviorSubject<number | null>(null)
  private onDestroy$ = new Subject<void>()

  public get currentlyEditingIndex(): number | null {
    return this.editingIndex$.value
  }

  public set currentlyEditingIndex(index: number | null) {
    this.editingIndex$.next(index)
  }

  private readonly cdm$ = combineLatest([
    this.filterChargeCtrl.valueChanges.pipe(startWith('')),
    this.changes$.pipe(startWith(null)),
  ]).pipe(
    debounceTime(300),
    switchMap(([searchValue]) => {
      this.loading$.next(true)
      // CDM identifiers are stored upper-cased and DynamoDB `contains` is case-sensitive.
      const term = (searchValue || '').trim().toUpperCase()
      let charge: FormGroup<ChargeRowFormGroup>

      if (this.currentlyEditingIndex !== null) {
        charge = this.chargesFormArray.at(this.currentlyEditingIndex)
      } else {
        charge = last(this.chargesFormArray.controls)
      }

      const type = charge.get('type').value
      const searchParams = {
        type,
        search: term ? term : undefined,
        facilityIds: [],
      }

      return this.restService.searchChargeDescriptionMaster(searchParams).pipe(
        // Keep the outer stream alive; shareReplay would otherwise replay the error forever.
        catchError(() => of([] as ChargeDescriptionMasterData[])),
        tap(() => this.loading$.next(false)),
      )
    }),
    takeUntil(this.onDestroy$),
    shareReplay(1),
  )

  // The "already on another row" filter is applied downstream of the cache so it re-evaluates when
  // the edited row changes. Inside the cache it would replay a list computed while the row was not
  // being edited — which omits that row's own charge, leaving the picker blank until the next
  // search lands.
  public filteredCdm$ = combineLatest([this.cdm$, this.editingIndex$]).pipe(
    map(([cdm, editingIndex]) => {
      const takenIds = (this.chargesFormArray?.controls ?? [])
        .filter((_, index) => index !== editingIndex)
        .map(c => c.value.chargeId)
      return cdm.filter((item) => !takenIds.includes(item.id))
    }),
  )

  private facilities$ = this.clinicService.getLocations().pipe(
    takeUntil(this.onDestroy$),
    shareReplay(1),
  )

  hbFacilities$ = this.facilities$.pipe(
    takeUntil(this.onDestroy$),
    combineLatestWith(this.changes$.pipe(
      takeUntil(this.onDestroy$),
      startWith(null),
    )),
    map(([facilities]) => facilities.filter((f) => this.facilityIds.includes(f.id))),
    shareReplay(1),
  )

  pbFacilityId: string

  pbFacilities$ = this.facilities$.pipe(
    takeUntil(this.onDestroy$),
    map(facilities => facilities.filter(facility => facility.mmx?.pbFacilityId != null)),
    tap(facilities => {
      if (facilities.length > 0) {
        this.pbFacilityId = facilities[0].id
      }
    }),
    shareReplay(1),
  )

  hbEnabled$: Observable<boolean> = this.clinicService.clinic$.pipe(
    takeUntil(this.onDestroy$),
    combineLatestWith(this.hbFacilities$),
    map(([clinic, facilities]) => clinic.pricer.hb && facilities.some(f => f.mmx?.hbFacilityId != null)),
    distinctUntilChanged(),
    shareReplay(1),
  )

  pbEnabled$: Observable<boolean> = this.clinicService.clinic$.pipe(
    takeUntil(this.onDestroy$),
    map(clinic => clinic.pricer.pb),
    distinctUntilChanged(),
    shareReplay(1),
  )

  constructor(
    private restService: ApiService,
    private clinicService: ClinicService,
    private permissionsService: PermissionsService,
    public estimateService: EstimateService,
  ) { }

  ngOnInit() {
    this.estimateService.chargesArray.next(this.chargesFormArray)
    this.syncVisitNumberValidators()

    this.clinic = this.clinicService.clinic

    this.permissionsService.hasPermission('appointment.estimate.charge.view')
      .pipe(
        takeUntil(this.onDestroy$),
      ).subscribe(isAllow => {
        if (isAllow) {
          this.displayedColumns = ['type', 'description', 'cptCode', 'modifiers', 'revCode', 'unit', 'totalCharge', 'actionsColumn']
        }
      })

    this.chargesFormArray.valueChanges
      .pipe(
        takeUntil(this.onDestroy$),
      )
      .subscribe(() => {
        this.chargesFormArray.controls.forEach((_, index) => {
          this.calculateRowTotal(index)
        })
      })

    this.chargesFormArray.parent.valueChanges.pipe(
      takeUntil(this.onDestroy$),
      debounceTime(300),
    ).subscribe(() => {
      this.visitNumbersValidator()
    })
  }

  onItemSelected(item: ChargeDescriptionMasterData, index: number) {
    const estimateItem = this.chargesFormArray.at(index)
    if (estimateItem) {
      estimateItem.patchValue({
        chargeId: item.id,
        chargeCode: item.chargeCode,
        code: item.code,
        type: item.type,
        description: item.chargeDescription,
        cptCode: item.cptCode,
        // Populated only when the charge code unambiguously encodes one; ambiguous tails stay blank.
        modifiers: (item.codeModifiers ?? []).join(','),
        revCode: item.revCode,
        quantity: 1,
        unitCharge: item.unitCharge,
        totalCharge: 0,
      })
      this.currentlyEditingIndex = null
      this.disableControls(estimateItem, ['type', 'description', 'cptCode'])
      this.valueUpdated.emit()
    }
  }

  onItemTypeSelected(type: ChargeDescriptionMaster.TYPE, index: number) {
    const estimateItem = this.chargesFormArray.at(index)
    if (estimateItem) {
      estimateItem.patchValue({
        type: type,
      })
      this.enableControls(estimateItem, ['description', 'cptCode'])
    }
    // Re-run the CDM search so the options match the new pricing type.
    this.changes$.next()
  }

  addEstimateItem(pricerType: ChargeDescriptionMaster.TYPE): void {
    this.filterChargeCtrl.setValue(null)

    const defaultVisitNumbers = this.appointmentVisitNumbers?.length > 0
      ? this.appointmentVisitNumbers
      : [1]
    const estimateItem = this.createEstimateItem({
      facilityId: pricerType === ChargeDescriptionMaster.TYPE.PB ? this.pbFacilityId : undefined,
      visitNumbers: this.showVisitNumbers
        ? this.noAppointmentsSelected ? [1] : defaultVisitNumbers
        : [],
    })

    this.chargesFormArray.push(estimateItem)

    this.onItemTypeSelected(pricerType, this.chargesFormArray.length - 1)

    // setTimeout — wait for the new row to land in the DOM before scrolling.
    setTimeout(() => {
      const element = this.chargeListElement.nativeElement
      element.scrollTop = element.scrollHeight
    }, 200)
    this.visitNumbersValidator()
  }

  removeEstimateItem(index: number): void {
    this.chargesFormArray.removeAt(index)
    this.valueUpdated.emit()
    this.visitNumbersValidator()
  }

  editEstimateItem(index: number): void {
    this.currentlyEditingIndex = index
    const estimateItem = this.chargesFormArray.at(index)
    if (estimateItem) {
      this.onItemTypeSelected(estimateItem.get('type').value, index)
      // Seed the CDM search with the current selection so the re-pick list renders the
      // existing charge (matched via compareWith) instead of an empty picker.
      this.filterChargeCtrl.setValue(estimateItem.get('description').value)
    }
  }

  /** The codes shown alongside the description, de-duplicated when CPT and charge code agree. */
  public chargeCodes(charge: Pick<ChargeDescriptionMasterData, 'cptCode' | 'chargeCode'>): string[] {
    return [charge?.cptCode, charge?.chargeCode].filter((code, index, codes) => !!code && codes.indexOf(code) === index)
  }

  public rowChargeCodes(index: number): string[] {
    const row = this.chargesFormArray.at(index)
    return this.chargeCodes({ cptCode: row?.get('cptCode')?.value, chargeCode: row?.get('chargeCode')?.value })
  }

  public rowChargeSummary(index: number): string {
    const row = this.chargesFormArray.at(index)
    return [row?.get('description')?.value, ...this.rowChargeCodes(index)].filter(Boolean).join(' · ')
  }

  // The picker's options are CDM objects while the control holds the description string, so the
  // default `===` never matches and edit mode would render blank. Descriptions repeat across CDM
  // rows (e.g. 96372V1/96372V2), so prefer the id of the row being edited before falling back.
  public compareChargeOption = (
    option: Pick<ChargeDescriptionMasterData, 'id' | 'chargeDescription'> | string | null,
    value: string | null,
  ): boolean => {
    if (option == null || value == null) {
      return option === value
    }
    if (typeof option !== 'string') {
      const editingChargeId = this.editingChargeId()
      if (editingChargeId && option.id) {
        return option.id === editingChargeId
      }
    }
    const description = typeof option === 'string' ? option : option.chargeDescription
    return description === value
  }

  private editingChargeId(): string | null {
    if (this.currentlyEditingIndex === null) {
      return null
    }
    return this.chargesFormArray.at(this.currentlyEditingIndex)?.get('chargeId')?.value || null
  }

  cancelEditEstimateItem(index: number): void {
    this.currentlyEditingIndex = null
    const estimateItem = this.chargesFormArray.at(index)
    if (estimateItem) {
      this.disableControls(estimateItem, ['type', 'description', 'cptCode'])
    }
  }

  onAddChargeDiscountControl(type: EstimateChargeDiscountType, index: number): void {
    this.addChargeDiscountControl.emit({ index, type })
  }

  onRemoveChargeDiscountControl(index: number): void {
    this.removeChargeDiscountControl.emit({ index })
  }

  public ngOnChanges(changes: SimpleChanges) {
    this.syncVisitNumberValidators()
    this.changes$.next()
    if ('readOnly' in changes || 'chargesFormArray' in changes) {
      this.applyReadOnly()
    }
  }

  // Only the always-editable controls — type/description/cptCode/revCode/unitCharge/totalCharge
  // are managed by the add/edit flow's disable() and must not be re-enabled by readOnly toggling.
  private static readonly READ_ONLY_TOGGLE_CONTROLS = ['modifiers', 'quantity', 'visitNumbers', 'cptDiscount', 'discountedUnits']

  private applyReadOnly(): void {
    if (!this.chargesFormArray) {
      return
    }
    for (const row of this.chargesFormArray.controls) {
      for (const name of ChargesTableFormComponent.READ_ONLY_TOGGLE_CONTROLS) {
        const control = row.get(name)
        if (!control) {
          continue
        }
        if (this.readOnly) {
          control.disable({ emitEvent: false })
        } else {
          control.enable({ emitEvent: false })
        }
      }
    }
  }

  public ngOnDestroy() {
    this.onDestroy$.next()
    this.onDestroy$.complete()
  }

  private createEstimateItem(item?: Partial<EstimateCharge>): FormGroup<ChargeRowFormGroup> {
    return new FormGroup<ChargeRowFormGroup>({
      chargeId: new FormControl(item?.chargeId || ''),
      chargeCode: new FormControl(item?.chargeCode || ''),
      code: new FormControl(item?.code || ''),
      type: new FormControl({ value: item?.type, disabled: true }, Validators.required),
      description: new FormControl({ value: item?.description || '', disabled: true }, Validators.required),
      cptCode: new FormControl({ value: item?.cptCode || '', disabled: true }, Validators.required),
      modifiers: new FormControl(item?.modifiers?.join(',') || ''),
      revCode: new FormControl({ value: item?.revCode || '', disabled: true }),
      quantity: new FormControl(item?.quantity ?? 1, Validators.required),
      unitCharge: new FormControl({ value: item?.unitCharge || 0, disabled: true }),
      totalCharge: new FormControl({ value: item?.unitCharge || 0, disabled: true }),
      facilityId: new FormControl(item?.facilityId),
      visitNumbers: new FormControl(
        item?.visitNumbers && item.visitNumbers.length > 0 ? item.visitNumbers : this.showVisitNumbers ? [1] : [],
        this.showVisitNumbers ? Validators.required : null,
      ),
    })
  }

  private syncVisitNumberValidators() {
    this.chargesFormArray?.controls.forEach((charge) => {
      const control = charge.controls.visitNumbers
      if (this.showVisitNumbers) {
        control.setValidators(Validators.required)
      } else {
        control.clearValidators()
      }
      control.updateValueAndValidity({ emitEvent: false })
    })
  }

  private disableControls(formGroup: FormGroup<ChargeRowFormGroup>, controlNames: string[]): void {
    controlNames.forEach(controlName => {
      const control = formGroup.get(controlName)
      if (control) {
        control.disable()
      }
    })
  }

  private enableControls(formGroup: FormGroup<ChargeRowFormGroup>, controlNames: string[]): void {
    controlNames.forEach(controlName => {
      const control = formGroup.get(controlName)
      if (control) {
        control.enable()
      }
    })
  }

  private calculateRowTotal(index: number): void {
    const chargeItem = this.chargesFormArray.at(index) as FormGroup
    const rowTotal = chargeItem.get('unitCharge').value * chargeItem.get('quantity').value
    chargeItem.get('totalCharge').setValue(rowTotal, { emitEvent: false }) // avoid re-triggering valueChanges
  }

  public parseVisitNumbers(value: string, index: number) {
    const control = this.chargesFormArray.at(index).get('visitNumbers')

    // `setValue({emitEvent:false})` + `setErrors(...)` both bypass the parent form's
    // valueChanges/statusChanges, so consumers gated on those (e.g. canCalculate$) never
    // re-evaluate after a free-text edit — emit valueUpdated to nudge them.
    const fail = (): void => {
      control.setErrors({ invalidVisitNumbers: true })
      this.visitNumbersValidator()
      this.valueUpdated.emit()
    }

    if (!value) {
      control?.setValue([], { emitEvent: false })
      control.setErrors(null)
      this.visitNumbersValidator()
      this.valueUpdated.emit()
      return
    }

    const VALID_PATTERN = /^\d+(?:-\d+)?(?:,\d+(?:-\d+)?)*$/

    if (!VALID_PATTERN.test(value)) {
      fail()
      return
    }

    const visits: number[] = []

    for (const part of value.split(',')) {
      const trimmed = part.trim()

      if ((trimmed.match(/-/g) || []).length > 1) {
        fail()
        return
      }

      if (trimmed.includes('-')) {
        const [startStr, endStr] = trimmed.split('-')

        const start = Number(startStr)
        const end = Number(endStr)

        if (!Number.isInteger(start) || !Number.isInteger(end) || end < start) {
          fail()
          return
        }

        for (let i = start; i <= end; i++) {
          visits.push(i)
        }

      } else {
        const n = Number(trimmed)

        if (!Number.isInteger(n)) {
          fail()
          return
        }

        visits.push(n)
      }
    }

    const normalized = Array.from(new Set(visits)).sort((a, b) => a - b)
    const upperBound = this.maxVisitNumber ?? this.appointmentVisitNumbers.length
    const tooSmall = normalized.some(n => n < 1)
    const tooLarge = upperBound > 0 && normalized.some(n => n > upperBound)
    if (tooSmall || tooLarge) {
      fail()
      return
    }
    control?.setValue(normalized, { emitEvent: false })
    control.setErrors(null)

    this.visitNumbersValidator()
    this.valueUpdated.emit()
  }

  public formatVisitNumbers(index: number): string {
    const visits: number[] =
    this.chargesFormArray.at(index).get('visitNumbers')?.value ?? []

    if (!visits.length) {
      return ''
    }

    const sorted = Array.from(new Set(visits)).sort((a, b) => a - b)

    const ranges: string[] = []

    let start = sorted[0]
    let end = sorted[0]

    for (let i = 1; i < sorted.length; i++) {
      const current = sorted[i]

      if (current === end + 1) {
        end = current
      } else {
        ranges.push(start === end ? `${start}` : `${start}-${end}`)
        start = end = current
      }
    }

    ranges.push(start === end ? `${start}` : `${start}-${end}`)

    return ranges.join(',')
  }

  private visitNumbersValidator() {
    if (!this.showVisitNumbers) {
      this.estimateService.selectedVisitNumbers$.next([])
      this.chargeMissingVisitNumber = false
      this.allVisitNumbersApplied = true
      this.invalidInputString = false
      this.estimateService.visitNumbersValid = true
      this.estimateService.checkCalcDisabledState$.next()
      return
    }

    const appliedVisitNumbers = new Set<number>()

    this.chargesFormArray.controls.forEach(charge => {
      const visitNumbers: number[] = charge.controls.visitNumbers.value ?? []

      let filteredVisitNumbers = visitNumbers

      if (!this.noAppointmentsSelected) {
        filteredVisitNumbers = visitNumbers.filter(vn =>
          this.appointmentVisitNumbers.includes(vn),
        )
      }

      filteredVisitNumbers.forEach(vn =>
        appliedVisitNumbers.add(vn),
      )

      if (filteredVisitNumbers.length !== visitNumbers.length) {
        charge.controls.visitNumbers.setValue(filteredVisitNumbers)
      }
    })

    this.estimateService.selectedVisitNumbers$.next(
      Array.from(appliedVisitNumbers),
    )

    this.chargeMissingVisitNumber =
    this.chargesFormArray.length > 0 &&
    this.chargesFormArray.controls.some(
      c => (c.controls.visitNumbers.value?.length ?? 0) === 0,
    )

    this.allVisitNumbersApplied = this.noAppointmentsSelected ||
    this.chargesFormArray.length === 0 ||
    this.appointmentVisitNumbers.every(n =>
      appliedVisitNumbers.has(n),
    )
    this.invalidInputString = this.chargesFormArray.controls.some(c => c.get('visitNumbers')?.hasError('invalidVisitNumbers'))
    this.estimateService.visitNumbersValid = !this.invalidInputString && this.allVisitNumbersApplied && !this.chargeMissingVisitNumber

    this.estimateService.checkCalcDisabledState$.next()
  }

}
